

































/* eslint-disable class-methods-use-this */
import { Component, Prop } from "vue-property-decorator";
import { format } from "date-fns";
import locale from "date-fns/locale/de";
import colors from "vuetify/es5/util/colors";
import { Ref, computed } from "vue";
import { NewsItemDetail, Link, Place } from "./types.d";
import CdkNewsItemDetail from "./CdkNewsItemDetail.vue";
import SlcNewsItemProvider from "../SlcNewsItem/SlcNewsItemProvider.vue";
import { Ednote } from "../../store/modules/newsItems/types.d";
import {
  getPriorityFilterRules,
  getKeywordFillterRules,
  getGeoAreaFilterRules
} from "../../store/modules/newsItems/getters"; // REVIEW
import CdkSharing from "@/components/CdkSharing";
import CdkNewsItemDetailText from "./CdkNewsItemDetailText.vue";
import useKeywordTracking from "@/composition/useKeywordTracking";
import useShareViaEmail from "../SlcNewsItem/lib/useShareViaEmail";
import usePDFExport from "../SlcNewsItem/lib/usePDFExport";

@Component({
  setup(props) {
    const { trackKeywordClickEvent } = useKeywordTracking();
    const newsItem: Ref<NewsItemDetail> = computed(() => props.newsItem);
    // @ts-ignore
    const emailSharing = computed(() => useShareViaEmail(newsItem));
    // @ts-ignore
    const pdfExport = usePDFExport(newsItem);

    return { trackKeywordClickEvent, emailSharing, pdfExport };
  },
  components: {
    CdkNewsItemDetail,
    CdkNewsItemDetailText,
    CdkSharing
  }
})
export default class CdkNewsItemDetailProvider extends SlcNewsItemProvider {
  // eslint-disable-next-line prettier/prettier
  @Prop() declare newsItem: NewsItemDetail;

  @Prop({
    type: Array,
    required: false,
    default: () => []
  })
  textGenres?: any[];

  @Prop({
    type: Array,
    required: false,
    default: () => []
  })
  categories?: any[];

  @Prop({
    type: Boolean,
    required: false,
    default: false
  })
  showSharingOptions?: Boolean;

  plainText: string = "";

  getGoogleMapsUri(str: string): string {
    return encodeURI(`https://www.google.com/maps/search/?api=1&query=${str}`);
  }

  filterByRole(items: Ednote[], str: string): Ednote[] {
    const PREFIX = "http://g2.dpa.com/cv/ednoterole";

    return items.filter(({ role }) => role === `${PREFIX}/${str}`);
  }

  mapToText(item: Ednote): string {
    return item.text;
  }

  /**
   * Maps an Ednote to a contact w. name and role
   */
  mapToContact(item: Ednote): any {
    const { text } = item;
    // EC-891: If a role is present, it is separated by a colon
    const [role, nameAndContacts] = text.split(":");
    const [name] = nameAndContacts
      ? nameAndContacts.split(",") // If there is anything after a colon
      : ["", role]; // Contact without role/name
    return {
      ...(nameAndContacts ? { role, name } : {})
    };
  }

  get dateTime(): string {
    // @ts-ignore
    return format(this.newsItem.versionCreated, "YYYY-MM-DD HH:mm", {
      locale
    });
  }

  get by(): string {
    if (this.newsItem.by !== null) {
      return this.newsItem.by;
    }
    return "";
  }

  get genre(): string {
    // @ts-ignore
    const genres = this.newsItem.genre;

    // @ts-ignore
    return this.textGenres
      .filter(item => genres.some(genre => genre === item.id))
      .map(item => item.name)
      .join("");
  }

  /**
   * Filters subjets by category type.
   */
  get mappedCategories(): any[] {
    // @ts-ignore
    const categories = this.newsItem.subject.filter(
      ({ type }) => type === "category"
    );
    // @ts-ignore
    const mapped = this.categories.filter(item =>
      categories.some(category => category.uri === item.id)
    );

    return mapped;
  }

  /**
   * Maps the categories to the filter rule format that is
   * needed for communication with the search component
   */
  get categoryFilterRules(): any[] {
    return this.mappedCategories.map(({ id, name }) => ({
      type: "FILTER_CATEGORY",
      values: [id],
      text: name
    }));
  }

  /**
   * REVIEW: For now only full text search is supported,
   * in a later stage it could be its own filter rule, e.g.
   * "FILTER_SUBJECT"
   */
  get subjectFilterRules(): any[] {
    // @ts-ignore
    return this.newsItem.subject
      .filter(({ type }) => type === "dpasubject")
      .map(({ name }) => ({
        type: "FILTER_FULLTEXT",
        values: [name],
        text: name
      }));
  }

  // WIP: Needs adjustment
  get tags(): any[] {
    // @ts-ignore
    const { urgency, subject, keyword } = this.newsItem;

    return [
      ...getPriorityFilterRules(urgency),
      ...this.categoryFilterRules, // Inject resolved categories
      ...this.subjectFilterRules,
      ...getKeywordFillterRules(keyword),
      ...getGeoAreaFilterRules(subject)
    ];
  }

  get main(): string {
    const doc = new DOMParser().parseFromString(
      this.newsItem.htmlBody,
      "text/html"
    );
    const main = doc.querySelector("section.main");

    if (main !== null) {
      return main.innerHTML;
    }

    console.warn("No section.main found in 'htmlBody'"); // eslint-disable-line

    return "";
  }

  get links(): Link[] {
    return this.newsItem.link.map(({ href, title }) => ({
      href,
      title
    }));
  }

  get places(): Place[] {
    return this.newsItem.places.map(item => ({
      lines: item.name.split(",").map(line => line.trim()),
      href: this.getGoogleMapsUri(item.name)
    }));
  }

  get edservices(): string[] {
    return this.filterByRole(
      // @ts-ignore
      this.newsItem.ednote,
      "edservice"
    ).map(this.mapToText);
  }

  get editorialnotes(): string[] {
    return this.filterByRole(
      // @ts-ignore
      this.newsItem.ednote,
      "editorialnote"
    ).map(this.mapToText);
  }

  get dpaContacts(): string[] {
    return this.filterByRole(
      // @ts-ignore
      this.newsItem.ednote,
      "dpacontacts"
    ).map(this.mapToContact);
  }

  get dpaContactsText(): string[] {
    return this.filterByRole(
      // @ts-ignore
      this.newsItem.ednote,
      "dpacontacts"
    ).map(this.mapToText);
  }

  get hasNotes(): boolean {
    const keys = ["links", "places", "edservices", "editorialnotes"];

    return keys.reduce(
      (acc: boolean, val: string) => this[val].length > 0 || acc,
      false
    );
  }

  get isUrgent(): boolean {
    // @ts-ignore
    return this.newsItem.urgency === 1 || this.newsItem.urgency === 2;
  }

  get backgroundColor(): string {
    return this.isUrgent ? colors.red.lighten5 : "#fff";
  }

  /**
   * Retuns the mapped props fo a news item,
   * so thay can be passed to a render fucntion
   */
  get newsItemDetailProps(): any {
    const {
      guid,
      creditLine,
      wordCount,
      headline,
      body,
      embargoed
    } = this.newsItem;
    const [title] = headline; // inconsistent APIs
    const {
      by,
      dateTime,
      date,
      description,
      dpaContacts,
      dpaContactsText,
      editorialnotes,
      edservices,
      filterPassBadges,
      genre,
      hasNotes,
      isEmbargoed,
      isUrgent,
      links,
      main,
      places,
      status,
      tags,
      time,
      tooltip
    } = this;

    const pubStatus = status; // inconsistent APIs

    return {
      by,
      body,
      creditLine,
      date,
      dateTime,
      description,
      dpaContacts,
      dpaContactsText,
      editorialnotes,
      edservices,
      filterPassBadges,
      genre,
      guid,
      hasNotes,
      isEmbargoed,
      ...(isEmbargoed
        ? {
            embargoed: `${format(embargoed, "DD. MMMM, HH:mm", { locale })} Uhr`
          }
        : {}),
      isUrgent,
      links,
      main,
      places,
      pubStatus,
      tags,
      time,
      title,
      tooltip,
      wordCount
    };
  }

  /**
   * Update the plain text rendering to be passed to sharing
   */
  handlePlainText(payload): void {
    this.plainText = payload;
  }

  /**
   * Forward tag click event to parent scope
   */
  tagClick(payload): void {
    this.$emit("tagClick", payload);
  }
}
